/*
 * The firmware form shared by the "add firmware" and the "firmware detail"
 * dialogs: its shape, how a row fills it, validation, and the request body.
 */
import { FirmwareItem } from '../../types/otaUpdate';

export const COMPONENT_TYPES = ['MAIN', 'WWWDATA', 'GSM'];

export interface FirmwareFormData {
  model: string;
  type: string;
  version: string;
  file_url: string;
  from_version: string;
  file_size: string;
  checksum: string;
  changelog: string;
  is_active: boolean;
  client_id: string;
}

export type FirmwareFormErrors = Record<string, string>;

export interface ModelOption {
  code: string;
  name: string;
  hardwareType: string;
}

export interface ClientOption {
  id: string;
  name: string;
}

export const emptyFirmwareForm = (): FirmwareFormData => ({
  model: '',
  type: 'MAIN',
  version: '',
  file_url: '',
  from_version: '',
  file_size: '',
  checksum: '',
  changelog: '',
  is_active: true,
  client_id: '',
});

export const firmwareToForm = (fw: FirmwareItem): FirmwareFormData => ({
  model: fw.model || '',
  type: fw.type || '',
  version: fw.version || '',
  file_url: fw.file_url || '',
  from_version: fw.from_version || '',
  file_size: fw.file_size?.toString() || '',
  checksum: fw.checksum || '',
  changelog: fw.changelog || '',
  is_active: fw.is_active,
  client_id: fw.client_id || '',
});

/** "PGA-01 - PGA Model 01 (PGA)": the code, the name when it differs, the gateway type. */
export const modelOptionLabel = (m: ModelOption): string => `${m.code}${m.name !== m.code ? ` - ${m.name}` : ''} (${m.hardwareType})`;

type Translate = (key: any, params?: Record<string, string | number>) => string;

export const validateFirmwareForm = (form: FirmwareFormData, t: Translate): FirmwareFormErrors => {
  const errors: FirmwareFormErrors = {};
  if (!form.model.trim()) errors.model = t('otaUpdates.validation.modelRequired');
  if (!form.type.trim()) errors.type = t('otaUpdates.validation.typeRequired');
  if (!form.version.trim()) errors.version = t('otaUpdates.validation.versionRequired');
  else if (form.version.length > 50) errors.version = t('otaUpdates.validation.versionMax');
  if (!form.file_url.trim()) errors.file_url = t('otaUpdates.validation.fileUrlRequired');
  else if (!/^https?:\/\//.test(form.file_url)) errors.file_url = t('otaUpdates.validation.fileUrlScheme');
  if (form.file_size && Number.isNaN(parseInt(form.file_size, 10))) errors.file_size = t('otaUpdates.validation.fileSizeNumber');
  return errors;
};

/** The body of POST and PATCH /v2/firmware: the required fields, plus the optional ones that were filled. */
export const buildFirmwarePayload = (form: FirmwareFormData): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    model: form.model.trim(),
    type: form.type.trim(),
    version: form.version.trim(),
    file_url: form.file_url.trim(),
    is_active: form.is_active,
  };
  if (form.from_version.trim()) payload.from_version = form.from_version.trim();
  if (form.file_size.trim()) payload.file_size = parseInt(form.file_size, 10);
  if (form.checksum.trim()) payload.checksum = form.checksum.trim();
  if (form.changelog.trim()) payload.changelog = form.changelog.trim();
  if (form.client_id) payload.client_id = form.client_id;
  return payload;
};

/** The API's detail when it is readable, otherwise the given fallback. */
export const explainFirmwareError = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg).join(', ');
  if (detail) return JSON.stringify(detail);
  return fallback;
};
